import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import * as api from '../api/client';
import { setAuthToken } from '../api/client';

const STORAGE_KEY = 'mindlyfriend.auth';

const AuthContext = createContext(null);

/**
 * Reads the persisted session from either store.
 *
 * "Keep me logged in" picks the backend: localStorage survives a browser
 * restart, sessionStorage dies with the tab. Both are checked on read because
 * we don't know which one the last login used.
 *
 * Wrapped in try/catch because storage access throws outright in Safari private
 * mode and in some embedded webviews — a storage failure should log the user
 * out, not crash the app on boot.
 */
function readStoredSession() {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      const raw = store.getItem(STORAGE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.token) return parsed;
    } catch {
      // Try the other store.
    }
  }
  return null;
}

function clearStoredSession() {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      store.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to do — the in-memory session is already cleared.
    }
  }
}

function writeStoredSession(session) {
  // Always clear both first, so toggling "keep me logged in" between logins
  // can't leave a stale token behind in the store we're no longer using.
  clearStoredSession();
  if (!session) return;

  try {
    // `remember` travels inside the session, so a later revalidation rewrites
    // it to the same store the user originally chose.
    const store = session.remember === false ? window.sessionStorage : window.localStorage;
    store.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage unavailable: the session still works for this tab, it just won't
    // survive a reload. Not worth failing the login over.
  }
}

/**
 * Holds the signed-in customer.
 *
 * The token lives in localStorage so a reload keeps you signed in. That is
 * readable by any script on the page, so an XSS bug becomes a session
 * compromise — the standard tradeoff for a token-based SPA. Moving to an
 * httpOnly, SameSite=Strict cookie removes that exposure but requires
 * `supports_credentials` with an explicit CORS origin plus CSRF tokens on
 * every mutating request. See the README.
 */
export function AuthProvider({ children }) {
  const stored = readStoredSession();

  // Seed the API client synchronously, before any child effect can fire a
  // request that would otherwise go out unauthenticated on first paint.
  if (stored?.token) setAuthToken(stored.token);

  const [user, setUser] = useState(stored?.user ?? null);
  const [token, setToken] = useState(stored?.token ?? null);
  // Only "checking" when there is a stored token worth revalidating.
  const [initialising, setInitialising] = useState(Boolean(stored?.token));

  const applySession = useCallback((session) => {
    setAuthToken(session?.token ?? null);
    setUser(session?.user ?? null);
    setToken(session?.token ?? null);
    writeStoredSession(session);
  }, []);

  // Revalidate a restored token once on mount. The stored user is shown
  // immediately so there's no auth flicker; this just corrects it if the token
  // has expired or the account changed server-side.
  useEffect(() => {
    if (!stored?.token) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const result = await api.fetchCurrentUser();
        if (cancelled) return;
        applySession({
          user: result.user,
          token: stored.token,
          remember: stored.remember,
        });
      } catch (err) {
        if (cancelled) return;
        if (err.status === 401) {
          // Token expired or was signed with a different secret.
          applySession(null);
        }
        // Anything else (backend down, network) — keep the cached session
        // rather than logging someone out because the server hiccuped.
      } finally {
        if (!cancelled) setInitialising(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally mount-only: `stored` is read once at construction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signup = useCallback(
    async (payload) => {
      const result = await api.signup(payload);
      applySession({ user: result.user, token: result.token, remember: true });
      return result.user;
    },
    [applySession]
  );

  // `remember: false` keeps the session in sessionStorage, so it ends with the
  // tab instead of persisting across browser restarts.
  const login = useCallback(
    async ({ remember = true, ...credentials }) => {
      const result = await api.login(credentials);
      applySession({ user: result.user, token: result.token, remember });
      return result.user;
    },
    [applySession]
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // The server call is a courtesy — these tokens are stateless. Clearing
      // the client session is what actually signs you out.
    }
    applySession(null);
  }, [applySession]);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user),
      initialising,
      signup,
      login,
      logout,
    }),
    [user, token, initialising, signup, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return context;
}

export default AuthContext;
